import React from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { EditOutlined, ScanOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../utils/currency';
import { formatUnit } from '../../../utils/units';
import type { WorkflowInvoiceIssue } from '../workflowInvoiceValidation';
import type { WorkflowInvoice } from '../../../types/workflowInvoice';

export interface InvoiceDraftSummaryProps {
  draft: WorkflowInvoice;
  editable: boolean;
  dirty: boolean;
  busy: boolean;
  extracting: boolean;
  conflict: boolean;
  filePresent: boolean;
  issues: WorkflowInvoiceIssue[];
  showApply: boolean;
  sourceCount: number;
  onEdit: () => void;
  onUpload: () => void;
  onRequestExtraction: () => void;
  onDownloadFile: (sha256: string) => void;
  onApply: () => void;
}

/**
 * Quiet minimal draft summary: counterparty, document number/status, totals
 * (unknown is never zero; the OCR printed TOTAL stays visibly separate from
 * the gross), compact lines preview and attachments. The full form lives in
 * the modal. Counterparty never crosses sides: a missing seller on a purchase
 * draft is an unknown seller, never our own buyer entity.
 */
const InvoiceDraftSummary: React.FC<InvoiceDraftSummaryProps> = ({
  draft,
  editable,
  dirty,
  busy,
  extracting,
  conflict,
  filePresent,
  issues,
  showApply,
  sourceCount,
  onEdit,
  onUpload,
  onRequestExtraction,
  onDownloadFile,
  onApply,
}) => {
  const { t } = useTranslation(['invoices', 'common']);
  const counterpartyName = draft.direction === 'PURCHASE' ? draft.sellerName : draft.buyerName;
  const counterpartyTaxId = draft.direction === 'PURCHASE' ? draft.sellerTaxId : draft.buyerTaxId;
  const approvedTotal = draft.totalGross ?? null;
  const review = draft.extractionReview ?? null;
  const hasReviewTotal = review !== null && review.documentTotal !== null && review.documentTotal !== undefined;
  const linesPreview = draft.lines.slice(0, 3);
  // A single accessible file row: the legacy file name plus the file list,
  // deduplicated by name so the same attachment never appears twice.
  const listedNames = new Set((draft.sourceFiles ?? []).map(file => file.name));
  const legacyFile = draft.sourceFileName && !listedNames.has(draft.sourceFileName)
    ? draft.sourceFileName
    : null;
  const files = draft.sourceFiles ?? [];
  const actionsDisabled = busy || extracting || conflict;

  return (
    <div className="invoice-summary" data-testid="invoice-summary">
      <div className="invoice-summary-top">
        <div className="invoice-summary-identity">
          <span className="invoice-summary-eyebrow">
            {t(`invoices:workflow.direction.${draft.direction.toLowerCase()}`)}
            {' · '}
            {t(`invoices:workflow.kind.${draft.kind.toLowerCase()}`)}
          </span>
          <h2 className="invoice-summary-number">
            {draft.originalDocumentNumber ?? t(`invoices:workflow.page.${draft.direction === 'PURCHASE' ? 'title' : 'salesTitle'}`)}
          </h2>
          <span className="invoice-summary-counterparty">
            {counterpartyName ?? t('invoices:workflow.summary.unknownCounterparty')}
            {' · '}
            {counterpartyTaxId ?? t('invoices:workflow.summary.unknownTaxId')}
          </span>
        </div>
        <div className="invoice-summary-status">
          <Tag color={extracting ? 'processing' : draft.extractionStatus === 'SUCCESS' ? 'success' : 'default'} role="status">
            {t(`invoices:workflow.extraction.${extracting ? 'pending' : draft.extractionStatus.toLowerCase()}`)}
          </Tag>
          <Tag color={draft.status === 'DRAFT' ? 'default' : 'success'}>
            {t(`invoices:workflow.page.status.${draft.status.toLowerCase()}`)}
          </Tag>
          {draft.manuallyCorrected && editable && (
            <Tag color="orange">{t('invoices:workflow.manuallyCorrected')}</Tag>
          )}
          {dirty && (
            <Tag color="gold" data-testid="invoice-summary-unsaved">
              {t('invoices:workflow.summary.unsaved')}
            </Tag>
          )}
          {editable && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={onEdit}
              disabled={busy}
              data-testid="invoice-edit-details"
            >
              {dirty ? t('invoices:workflow.summary.reopenDetails') : t('invoices:workflow.summary.editDetails')}
            </Button>
          )}
        </div>
      </div>

      <dl className="invoice-summary-facts">
        <div><dt>{t('invoices:workflow.fields.issueDate')}</dt><dd>{draft.issueDate || '—'}</dd></div>
        <div><dt>{t('invoices:workflow.fields.totalNet')}</dt><dd>{draft.totalNet != null && draft.currency ? formatCurrency(draft.totalNet, draft.currency) : '—'}</dd></div>
        <div><dt>{t('invoices:workflow.fields.totalVat')}</dt><dd>{draft.totalVat != null && draft.currency ? formatCurrency(draft.totalVat, draft.currency) : '—'}</dd></div>
        <div><dt>{t('invoices:workflow.fields.exchangeRate')}</dt><dd>{draft.currency === 'PLN' ? '1' : draft.exchangeRateToPLN && draft.exchangeRateToPLN > 0 ? draft.exchangeRateToPLN.toLocaleString('pl-PL', { maximumFractionDigits: 8 }) : '—'}</dd></div>
      </dl>
      <div className="invoice-summary-totals">
        <div className="invoice-summary-total">
          <span className="invoice-summary-label">{t('invoices:workflow.summary.approvedTotal')}</span>
          <span className="invoice-summary-amount" data-testid="invoice-summary-total">
            {approvedTotal !== null && draft.currency
              ? formatCurrency(approvedTotal, draft.currency)
              : t('invoices:workflow.placeholders.unknown')}
          </span>
        </div>
        {hasReviewTotal && (
          <div className="invoice-summary-ocr">
            <span className="invoice-summary-label">{t('invoices:workflow.extractionReviewTotalLabel')}</span>
            <span className="invoice-summary-ocr-amount" data-testid="invoice-summary-ocr-total">
              {review!.currency
                ? formatCurrency(review!.documentTotal as number, review!.currency)
                : (review!.documentTotal as number).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="invoice-summary-ocr-note">{t('invoices:workflow.summary.ocrUnclassified')}</span>
          </div>
        )}
      </div>

      <div className="invoice-summary-lines" data-testid="invoice-summary-lines">
        <span className="invoice-summary-label">
          {t('invoices:workflow.linesTitle')}
          {draft.lines.length > 0 ? ` · ${draft.lines.length}` : ''}
        </span>
        {draft.lines.length === 0 ? (
          <Typography.Text type="secondary">{t('invoices:workflow.linesEmpty')}</Typography.Text>
        ) : (
          <ul className="invoice-summary-lines-list">
            {linesPreview.map((line) => (
              <li key={line.key} className="invoice-summary-line">
                <span className="invoice-summary-line-name">{line.productName ?? '—'}</span>
                <span className="invoice-summary-line-meta">
                  {line.quantity != null ? `${line.quantity} ${line.unit ? formatUnit(line.unit) : ''}`.trim() : t('invoices:workflow.placeholders.unknown')}
                  {line.grossAmount != null && draft.currency ? ` · ${formatCurrency(line.grossAmount, draft.currency)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        {draft.lines.length > linesPreview.length && (
          <Typography.Text type="secondary">
            {t('invoices:workflow.summary.moreLines', { count: draft.lines.length - linesPreview.length })}
          </Typography.Text>
        )}
      </div>

      {(legacyFile || files.length > 0) && (
        <div className="invoice-summary-files">
          <span className="invoice-summary-label">{t('invoices:workflow.summary.attachments')}</span>
          <div className="invoice-summary-file-row">
            {legacyFile && <span className="invoice-summary-file-name">{legacyFile}</span>}
            {files.map((file) => (
              <Button key={file.sha256} size="small" type="link" className="invoice-summary-file-name" onClick={() => onDownloadFile(file.sha256)}>
                {file.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {editable && (draft.extractionStatus === 'FAILED' || issues.length > 0) && (
        <div className="invoice-summary-notices">
          {draft.extractionStatus === 'FAILED' && (
            <p className="invoice-summary-notice invoice-summary-notice--error">
              <strong>{t('invoices:workflow.extractionFailed')}</strong>
              {' — '}
              {t('invoices:workflow.manualFallback')}
            </p>
          )}
          {issues.length > 0 && (
            <div className="invoice-summary-notice invoice-summary-notice--warning" role="alert">
              <strong>{t('invoices:workflow.issuesTitle')}</strong>
              <ul>{issues.map((issue) => <li key={`${issue.field}:${issue.key}`}><Button type="link" onClick={onEdit} disabled={busy}>{t(issue.key)}</Button></li>)}</ul>
            </div>
          )}
        </div>
      )}

      <div className="invoice-summary-actions">
        <Button type="link" size="small" onClick={onEdit} disabled={busy}>
          {t('invoices:workflow.summary.ordersLink')}
          {sourceCount > 0 ? ` · ${sourceCount}` : ''}
        </Button>
        {editable && (
          <Space wrap size={8}>
            <Button icon={<UploadOutlined />} onClick={onUpload} disabled={actionsDisabled} data-testid="invoice-upload-file">
              {t('invoices:workflow.actions.uploadSourceFile')}
            </Button>
            <Button icon={<ScanOutlined />} onClick={onRequestExtraction} disabled={actionsDisabled || !filePresent} loading={extracting} data-testid="invoice-request-extraction">
              {t('invoices:workflow.actions.requestExtraction')}
            </Button>
            {showApply && (
              <Button onClick={onApply} disabled={actionsDisabled} data-testid="invoice-apply-extraction">
                {t('invoices:workflow.page.applyExtraction')}
              </Button>
            )}
          </Space>
        )}
      </div>
    </div>
  );
};

export default InvoiceDraftSummary;
